import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import { AI_STUDIO_TABS } from '../../constants';
import {
  addFavoriteMedia,
  callAiStudio,
  getFavoriteMedia,
  getFreeTextVideoRatio,
  getImagesRatio,
  getVideoRatio,
  removeFavoriteMedia,
} from './aiStudioActions';

const initialState = {
  selectedMediaIndex: 0,
  bucket: null,

  requestsHistory: [],
  isRequestsHistoryLoading: false,
  selectedRequest: '',

  responses: [],
  isResponsesLoading: false,

  studioTab: AI_STUDIO_TABS.GENERATED,

  favoriteMedia: [],
  isFavoriteMediaLoading: false,
  favoriteTableSettings: {
    page: 0,
    size: 10,
    totalElements: 0,
  },

  imagesRatio: [],
  isImagesRatioLoading: false,
  videoRatio: [],
  isVideoRatioLoading: false,
  freeTextVideoRatio: [],
  isFreeTextVideoRatioLoading: false,
};

const aiStudioSlice = createSlice({
  name: 'aiStudio',
  initialState,
  reducers: {
    changeStudioTab: (state, action) => {
      state.studioTab = action.payload;
    },
    changeFavoriteTableSettings: (state, action) => {
      state.favoriteTableSettings = { ...state.favoriteTableSettings, ...action.payload };
    },
    setSelectedMediaIndex: (state, action) => {
      state.selectedMediaIndex = action.payload;
    },
    setBucket: (state, action) => {
      state.bucket = action.payload;
    },
    selectHistory: (state, action) => {
      state.selectedRequest = action.payload;
    },
    setRequestsHistory: (state, action) => {
      state.requestsHistory = action.payload;
    },
    setResponses: (state, action) => {
      state.responses = action.payload;
    },
    updateBucketItem: (state, action) => {
      const { id, newContent } = action.payload;
      state.bucket.media = state.bucket.media.map((item) => {
        if (item.id === id) {
          return { ...item, ...newContent };
        }
        return item;
      });
    },
    updateFavoriteMedia: (state, action) => {
      const { id, metadata } = action.payload;
      state.favoriteMedia = state.favoriteMedia.map((item) => {
        if (item.id === id) {
          return { ...item, ...metadata };
        }
        return item;
      });
    },

    addRequest: (state, action) => {
      state.requestsHistory = [action.payload, ...state.requestsHistory];
    },
    addResponse: (state, action) => {
      state.responses = [action.payload, ...state.responses];
    },
    addBucketMedia: (state, action) => {
      state.bucket.media = [action.payload, ...state.bucket.media];
      state.selectedMediaIndex = 0;
    },
    updateResponse: (state, action) => {
      const { id, newContent } = action.payload;

      state.responses = state.responses.map((item) => {
        if (item.id === id) {
          return { ...item, ...newContent };
        }
        return item;
      });
    },

    resetAiStudioWithoutRatios: (state) => {
      return {
        ...initialState,
        imagesRatio: state.imagesRatio,
        videoRatio: state.videoRatio,
        freeTextVideoRatio: state.freeTextVideoRatio,
      };
    },

    resetAiStudio: () => initialState,
  },
  extraReducers: (builder) => {
    //GET FAVORITE MEDIA
    builder.addCase(getFavoriteMedia.fulfilled, (state, action) => {
      state.isFavoriteMediaLoading = false;
      state.favoriteMedia = action.payload.content;
      state.favoriteTableSettings.totalElements = action.payload.totalElements;
    });
    builder.addCase(getFavoriteMedia.pending, (state) => {
      state.isFavoriteMediaLoading = true;
    });
    builder.addCase(getFavoriteMedia.rejected, (state) => {
      state.isFavoriteMediaLoading = false;
    });

    //ADD FAVORITE MEDIA
    builder.addCase(addFavoriteMedia.fulfilled, (state, action) => {
      state.responses = state.responses.map((item) => {
        if (item.id === action.payload.id) {
          return { ...item, favoriteId: action.payload.favoriteId, isFavorite: true };
        }
        return item;
      });
    });

    //REMOVE FAVORITE MEDIA
    builder.addCase(removeFavoriteMedia.fulfilled, (state, action) => {
      state.responses = state.responses.map((item) => {
        if (item.favoriteId === action.payload.id) {
          return { ...item, favoriteId: null, isFavorite: false };
        }
        return item;
      });
    });
    //IMAGES RATIO
    builder.addMatcher(isAnyOf(getImagesRatio.fulfilled), (state, action) => {
      state.isImagesRatioLoading = false;
      state.imagesRatio = action.payload;
    });
    builder.addMatcher(isAnyOf(getImagesRatio.pending), (state) => {
      state.isImagesRatioLoading = true;
    });
    builder.addMatcher(isAnyOf(getImagesRatio.rejected), (state) => {
      state.isImagesRatioLoading = false;
      state.imagesRatio = [];
    });

    //VIDEO RATIO
    builder.addMatcher(isAnyOf(getVideoRatio.fulfilled), (state, action) => {
      state.isVideoRatioLoading = false;
      state.videoRatio = action.payload;
    });
    builder.addMatcher(isAnyOf(getVideoRatio.pending), (state) => {
      state.isVideoRatioLoading = true;
    });
    builder.addMatcher(isAnyOf(getVideoRatio.rejected), (state) => {
      state.isVideoRatioLoading = false;
      state.videoRatio = [];
    });

    //FREE TEXT VIDEO RATIO
    builder.addMatcher(isAnyOf(getFreeTextVideoRatio.fulfilled), (state, action) => {
      state.isFreeTextVideoRatioLoading = false;
      state.freeTextVideoRatio = action.payload;
    });
    builder.addMatcher(isAnyOf(getFreeTextVideoRatio.pending), (state) => {
      state.isFreeTextVideoRatioLoading = true;
    });
    builder.addMatcher(isAnyOf(getFreeTextVideoRatio.rejected), (state) => {
      state.isFreeTextVideoRatioLoading = false;
      state.freeTextVideoRatio = [];
    });
    //CALL STUDIO
    builder.addMatcher(isAnyOf(callAiStudio.fulfilled), (state) => {
      state.isRequestsHistoryLoading = false;
      state.isResponsesLoading = false;
    });
    builder.addMatcher(isAnyOf(callAiStudio.pending), (state) => {
      state.isRequestsHistoryLoading = true;
      state.isResponsesLoading = true;
    });
    builder.addMatcher(isAnyOf(callAiStudio.rejected), (state) => {
      state.isRequestsHistoryLoading = false;
      state.isResponsesLoading = false;
    });
  },
});

export const {
  setSelectedMediaIndex,
  setBucket,
  setRequestsHistory,
  setResponses,
  updateBucketItem,
  selectHistory,
  addRequest,
  addResponse,
  updateResponse,

  changeStudioTab,
  changeFavoriteTableSettings,
  addBucketMedia,
  updateFavoriteMedia,

  resetAiStudioWithoutRatios,
  resetAiStudio,
} = aiStudioSlice.actions;

export default aiStudioSlice.reducer;
